import { useState } from 'react';
import { ExternalLink, Copy, Check, MoreVertical, Share } from 'lucide-react';
import { getInAppBrowser, getPlatform } from '../utils/browser';

// Vises når brukeren prøver å logge inn fra en in-app-nettleser
// (Snapchat, Instagram osv.) der Google-innlogging blokkeres.
// Forklarer at de må åpne siden i ekte nettleser, med kopier-lenke-knapp.
export default function InAppBrowserNotice({ onClose }) {
  const [copied, setCopied] = useState(false);
  const appName = getInAppBrowser() || 'denne appen';
  const platform = getPlatform();
  const url = window.location.origin + window.location.pathname;

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Ignorer — bruker kan kopiere manuelt
    }
  };

  // Plattform-spesifikk instruksjon for å åpne i ekte nettleser
  const instruction =
    platform === 'ios' ? (
      <span className="inline-flex items-center gap-1 flex-wrap justify-center">
        Trykk på <Share size={15} className="inline" /> eller
        <MoreVertical size={15} className="inline" /> oppe i hjørnet, og velg
        <strong>«Åpne i Safari»</strong>.
      </span>
    ) : platform === 'android' ? (
      <span className="inline-flex items-center gap-1 flex-wrap justify-center">
        Trykk på <MoreVertical size={15} className="inline" /> oppe i hjørnet, og
        velg <strong>«Åpne i Chrome»</strong> eller «Åpne i nettleser».
      </span>
    ) : (
      <span>Åpne lenken i en vanlig nettleser (Chrome, Safari, Edge).</span>
    );

  return (
    <div className="fixed inset-0 z-[600] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#F8F5F0] border-2 border-[#3B4D3C] rounded-lg shadow-2xl max-w-md w-full p-8 relative animate-in fade-in duration-300 text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-[#E67E22]/10 border border-[#E67E22]/30 rounded-full p-4">
            <ExternalLink size={32} className="text-[#E67E22]" />
          </div>
        </div>

        <h3 className="text-xl font-black italic text-[#3B4D3C] mb-3">
          Åpne i nettleseren din
        </h3>

        <p className="font-sans text-sm text-[#2D3436]/75 mb-4 leading-relaxed">
          Google tillater ikke innlogging inne i {appName}. For å logge inn,
          åpne FiskDa i din vanlige nettleser.
        </p>

        <div className="bg-white border border-[#D1CCC0] rounded-lg p-4 mb-5 font-sans text-sm text-[#3B4D3C]">
          {instruction}
        </div>

        <button
          onClick={copyLink}
          className="w-full px-6 py-3 bg-[#3B4D3C] text-[#F8F5F0] rounded shadow-md font-sans font-semibold hover:bg-[#2D3A2D] transition-all duration-300 flex items-center justify-center gap-2 mb-3"
        >
          {copied ? (
            <>
              <Check size={18} /> Lenke kopiert!
            </>
          ) : (
            <>
              <Copy size={18} /> Kopier lenke
            </>
          )}
        </button>
        <p className="font-sans text-xs text-[#2D3436]/50 mb-5 break-all">{url}</p>

        {onClose && (
          <button
            onClick={onClose}
            className="font-sans text-xs uppercase tracking-widest text-[#3B4D3C]/50 hover:text-[#3B4D3C] transition-colors"
          >
            Lukk
          </button>
        )}
      </div>
    </div>
  );
}
